import React, { useState } from 'react';
import { Box, Typography, Paper, Button, IconButton, Stack, CircularProgress } from '@mui/material';
import { Edit as EditIcon, Save as SaveIcon, Cancel as CancelIcon, Delete as DeleteIcon } from '@mui/icons-material';
import axios from '../../api/axios';
import MarkdownRenderer from '../Common/MarkdownRenderer';
import MarkdownEditor from '../Common/MarkdownEditor';

const Definition = ({ definition, isTeacher = false, notebookId = null, sectionTitle = null, blockIndex = 0, onUpdate = null }) => {
  const [editing, setEditing] = useState(false);
  const [editingContent, setEditingContent] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  if (!definition && !isTeacher) {
    return null;
  }

  const handleEdit = () => {
    setEditingContent(definition || '');
    setEditing(true);
  };

  const handleCancel = () => {
    setEditing(false);
    setEditingContent('');
  };

  const handleSave = async () => {
    if (!notebookId || !sectionTitle) {
      return;
    }

    try {
      setSaving(true);
      await axios.put(`/notes/${notebookId}/section/${encodeURIComponent(sectionTitle)}/concept-block/${blockIndex}`, {
        field: 'definition',
        value: editingContent
      });

      setEditing(false);
      setEditingContent('');

      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error('Error saving definition:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.details || error.message || '保存失败，请重试';
      alert('保存失败：' + errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!notebookId || !sectionTitle) {
      return;
    }
    
    // 确认删除
    if (!window.confirm('确定要删除定义吗？此操作不可撤销。')) {
      return;
    }
    
    try {
      setDeleting(true);
      await axios.put(`/notes/${notebookId}/section/${encodeURIComponent(sectionTitle)}/concept-block/${blockIndex}`, {
        field: 'definition',
        value: ''
      });
      
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error('Error deleting definition:', error);
      alert('删除失败，请重试');
    } finally {
      setDeleting(false);
    }
  };

  if (editing) {
    return (
      <Paper
        elevation={1}
        sx={{
          p: 3,
          mb: 3,
          borderLeft: '4px solid',
          borderColor: 'primary.main',
          backgroundColor: 'background.paper',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 600, color: 'primary.main', fontSize: '1.1rem' }}>
            定义
          </Typography>
          <Stack direction="row" spacing={1}>
            <IconButton
              size="small"
              color="primary"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? <CircularProgress size={20} /> : <SaveIcon fontSize="small" />}
            </IconButton>
            <IconButton
              size="small"
              color="default"
              onClick={handleCancel}
              disabled={saving}
            >
              <CancelIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Box>
        <MarkdownEditor
          value={editingContent}
          onChange={setEditingContent}
          label="定义内容"
          minRows={12}
          maxRows={20}
          showPreview={true}
          showToolbar={true}
          placeholder="输入Markdown格式的内容..."
          disabled={saving}
        />
      </Paper>
    );
  }

  if (!definition && !isTeacher) {
    return null;
  }

  if (!definition && isTeacher) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 3,
          border: '2px dashed',
          borderColor: 'divider',
          borderRadius: 1,
          textAlign: 'center',
          '&:hover': {
            borderColor: 'primary.main',
            backgroundColor: 'action.hover',
          },
        }}
      >
        <Button
          variant="outlined"
          startIcon={<EditIcon fontSize="small" />}
          onClick={handleEdit}
          sx={{ textTransform: 'none' }}
        >
          添加概念定义
        </Button>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={1}
      sx={{
        p: 3,
        mb: 3,
        borderLeft: '4px solid',
        borderColor: 'primary.main',
        backgroundColor: 'background.paper',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 600, color: 'primary.main', fontSize: '1.1rem' }}>
            定义
          </Typography>
        {isTeacher && (
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant="outlined"
              color="primary"
              startIcon={<EditIcon fontSize="small" />}
              onClick={handleEdit}
            >
              修改
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon fontSize="small" />}
              onClick={handleDelete}
              disabled={deleting}
            >
              删除
            </Button>
          </Stack>
        )}
      </Box>
      <Box sx={{ color: 'text.primary' }}>
        <MarkdownRenderer content={definition} />
      </Box>
    </Paper>
  );
};

export default Definition;
